import { curSym, activeCurrency, symbolForCurrency, CURRENCIES } from "@/lib/company";
import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { fmtTL, fmtCur } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ComposedChart, Line, Legend } from "recharts";
import {
  Landmark, TrendingUp, Receipt, Percent, Wallet, FileText, Users, Coins,
  Plus, Trash2, Building2, User, Eye, ArrowDownUp, FileSpreadsheet, FileDown, Printer,
  Mail, Send, Check, Bell, MessageCircle, Settings2, History,
  CalendarDays, RefreshCw, Globe, ChevronLeft, ChevronRight,
} from "lucide-react";
import { useSort } from "@/lib/sort";
import { scanQrFromFile, parseDocNoQr } from "@/lib/qr";
import { SortTh } from "@/components/SortTh";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";
import { useActiveLocation } from "@/context/ActiveLocationContext";
import WasteTab from "@/components/WasteTab";
import { printBlankTemplate } from "@/lib/templates";
import { isoDate, presetRange, EXP_CATS, INC_CATS, ACC_LABEL, Kpi, StmtRow, LiveFxButton } from "@/pages/accounting/shared";
import { IncomingTab, OutgoingTab } from "@/pages/accounting/InvoiceTabs";
import { FinanceDocA5, printFinanceNode } from "@/components/FinanceDocs";
import CompanyDocuments from "@/components/CompanyDocuments";

// Yardımcılar (isoDate, presetRange, EXP_CATS, INC_CATS, ACC_LABEL, Kpi, StmtRow) ve
// alt sekmeler (IncomingTab, OutgoingTab) → pages/accounting/* dosyalarına taşındı.
// Personel & Bordro artık ayrı modül: /personel (pages/Personnel.js).

const TAB_GROUPS = [
  { key: "genel", label: "Genel Bakış", tabs: [["overview", "Genel Bakış"]] },
  { key: "defter", label: "Defter & Cari", tabs: [["ledger", "Gelir / Gider"], ["cari", "Cari Hesaplar"], ["due", "Vadeler (Çek/Senet)"], ["calendar", "Vade Takvimi"]] },
  { key: "fatura", label: "Faturalar & KDV", tabs: [["incoming", "Gelen Faturalar"], ["outgoing", "Giden Faturalar"], ["vat", "KDV"]] },
  { key: "analiz", label: "Analiz", tabs: [["pl", "Kâr / Zarar"], ["channel", "Kanal Kârlılığı"], ["count", "Sayım Farkı"], ["aging", "Yaşlandırma"], ["fx", "Döviz / Kur Farkı"]] },
  { key: "belgeler", label: "Firma Belgeleri", tabs: [["company-documents", "Firma Belgeleri"]] },
];

function companyDocumentTenant(user) {
  try { return localStorage.getItem("groot_tenant") || user?.tenant || user?.tenant_slug || null; }
  catch (_) { return user?.tenant || user?.tenant_slug || null; }
}

export default function Accounting() {
  const { can, user } = useAuth();
  const canWrite = can("accounting.write");
  const documentTenant = companyDocumentTenant(user);
  const documentActor = user?.id || user?.email || null;
  const canReadDocuments = Boolean(documentTenant && documentActor && can("accounting.read"));
  const documentScope = JSON.stringify([documentTenant, documentActor, canReadDocuments, canWrite]);
  const canWaste = can("stock.adjust") || can("accounting.read");
  const groups = TAB_GROUPS.filter((g) => g.key !== "belgeler" || canReadDocuments).map((g) => (g.key === "analiz" && canWaste ? { ...g, tabs: [...g.tabs, ["waste", "Fire / Zayiat"]] } : g));
  const [tab, setTab] = useState("overview");
  const activeGroup = groups.find((g) => g.tabs.some(([id]) => id === tab)) || groups[0];
  const [openEntry, setOpenEntry] = useState(false);
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(presetRange("month"));
  const params = { from_date: `${range[0]}T00:00:00`, to_date: `${range[1]}T23:59:59.999999` };

  const setP = (k) => { setPreset(k); setRange(presetRange(k)); };
  const setCustom = (i, v) => { setPreset("custom"); setRange((r) => { const n = [...r]; n[i] = v; return n; }); };
  const PRESETS = [["today", "Bugün"], ["week", "Son 7 Gün"], ["month", "Bu Ay"], ["lastmonth", "Geçen Ay"], ["year", "Bu Yıl"]];

  return (
    <div className="space-y-4 fade-in" data-testid="accounting-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Landmark className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Muhasebe</p><h1 className="font-heading text-2xl font-bold text-slate-900">Muhasebe</h1><p className="mt-1 text-sm text-slate-500" data-testid="accounting-purpose">Mali kayıt & vergi — gelir/gider, KDV, kâr/zarar, cari, çek/senet, bordro. Dönemsel satış analizi <b>Raporlar</b>'da, canlı gün <b>Panel</b>'de.</p></div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1">
            {PRESETS.map(([k, l]) => (
              <button key={k} data-testid={`acc-preset-${k}`} onClick={() => setP(k)}
                className={`rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${preset === k ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <DatePickerTR testid="acc-from" value={range[0]} onChange={(v) => setCustom(0, v)} className="mt-0 h-9 w-40" />
          <span className="text-xs text-slate-400">–</span>
          <DatePickerTR testid="acc-to" value={range[1]} onChange={(v) => setCustom(1, v)} className="mt-0 h-9 w-40" />
          {canWrite && <Button data-testid="acc-new-entry-btn" onClick={() => { setTab("ledger"); setOpenEntry(true); }} className="ml-1 bg-brand-green text-white hover:bg-brand-green-600"><Plus className="mr-1.5 h-4 w-4" /> Yeni Kayıt</Button>}
        </div>
      </div>

      <div className="space-y-1.5 border-b border-slate-200 pb-1.5">
        <div className="flex flex-wrap gap-1" data-testid="acc-group-bar">
          {groups.map((g) => {
            const on = g.tabs.some(([id]) => id === tab);
            return (
              <button key={g.key} data-testid={`acc-group-${g.key}`} onClick={() => setTab(g.tabs[0][0])}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${on ? "bg-brand-green text-white" : "bg-slate-100 text-slate-500 hover:text-slate-800"}`}>{g.label}</button>
            );
          })}
        </div>
        {activeGroup && activeGroup.tabs.length > 1 && (
          <div className="flex flex-wrap gap-1 pt-0.5" data-testid="acc-subtab-bar">
            {activeGroup.tabs.map(([id, label]) => (
              <button key={id} data-testid={`acc-tab-${id}`} onClick={() => setTab(id)}
                className={`rounded-sm px-3 py-1 text-[13px] font-medium transition-colors ${tab === id ? "bg-brand-green/10 text-brand-green" : "text-slate-500 hover:text-slate-800"}`}>{label}</button>
            ))}
          </div>
        )}
      </div>

      {tab === "overview" && <OverviewTab params={params} onGoTab={setTab} />}
      {tab === "company-documents" && canReadDocuments && <CompanyDocuments key={documentScope} canWrite={canWrite} />}
      {tab === "ledger" && <LedgerTab range={range} canWrite={canWrite} openEntry={openEntry} setOpenEntry={setOpenEntry} />}
      {tab === "due" && <DueTab canWrite={canWrite} />}
      {tab === "calendar" && <CalendarTab />}
      {tab === "fx" && <FxReportTab />}
      {tab === "pl" && <PLTab params={params} />}
      {tab === "channel" && <ChannelProfitTab params={params} />}
      {tab === "count" && <CountDiffTab params={params} />}
      {tab === "aging" && <AgingTab />}
      {tab === "vat" && <VatTab params={params} />}
      {tab === "cari" && <CariTab range={range} canWrite={canWrite} />}
      {tab === "incoming" && <IncomingTab canWrite={canWrite} />}
      {tab === "outgoing" && <OutgoingTab canWrite={canWrite} />}
      {tab === "waste" && <WasteTab />}
    </div>
  );
}

function AgingTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const { can } = useAuth();
  const canExport = can("accounting.export");
  const [emailRow, setEmailRow] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [remOpen, setRemOpen] = useState(false);
  const [remCfg, setRemCfg] = useState({ enabled: true, days: 3 });
  const [remBusy, setRemBusy] = useState(false);
  const [tlRow, setTlRow] = useState(null);
  const [tlEvents, setTlEvents] = useState([]);
  const [tlBusy, setTlBusy] = useState(false);
  const openTimeline = async (r) => {
    setTlRow(r); setTlEvents([]); setTlBusy(true);
    try {
      const res = await api.get(`/accounting/cari/${r.cari_type}/${r.cari_id}/statement/timeline`);
      setTlEvents(res.data.events || []);
    } catch (e) { toast.error(apiError(e) || "Zaman çizelgesi alınamadı"); }
    finally { setTlBusy(false); }
  };
  const openRemConfig = async () => {
    try {
      const r = await api.get("/accounting/statement-reminder-config");
      setRemCfg({ enabled: r.data.enabled !== false, days: r.data.days || 3 });
    } catch { /* varsayılanları koru */ }
    setRemOpen(true);
  };
  const saveRemConfig = async () => {
    setRemBusy(true);
    try {
      const r = await api.put("/accounting/statement-reminder-config", { enabled: remCfg.enabled, days: Number(remCfg.days) || 3 });
      setRemCfg({ enabled: r.data.enabled, days: r.data.days });
      toast.success(r.data.enabled ? `Otomatik hatırlatma açık — ${r.data.days} gün sonra` : "Otomatik hatırlatma kapatıldı");
      setRemOpen(false);
    } catch (e) { toast.error(apiError(e) || "Ayar kaydedilemedi"); }
    finally { setRemBusy(false); }
  };
  const sendStatement = async () => {
    if (!emailRow) return;
    setEmailBusy(true);
    try {
      const res = await api.post(`/accounting/cari/${emailRow.cari_type}/${emailRow.cari_id}/statement/email`, { to: emailTo.trim() || undefined });
      toast.success(`Ekstre gönderildi: ${res.data.to}`);
      setEmailRow(null); setEmailTo(""); load();
    } catch (e) { toast.error(apiError(e) || "Ekstre e-postası gönderilemedi"); }
    finally { setEmailBusy(false); }
  };
  const bulkSend = async () => {
    setBulkBusy(true); setBulkResult(null);
    try {
      const res = await api.post(`/accounting/cari/statement/email-bulk`, { type, only_overdue: onlyOverdue });
      const d = res.data;
      setBulkResult(d);
      if (d.sent_count > 0) toast.success(`${d.sent_count} cariye ekstre gönderildi${d.skipped_count ? `, ${d.skipped_count} e-postasız atlandı` : ""}${d.failed_count ? `, ${d.failed_count} başarısız` : ""}`);
      else toast.message(`Gönderim yapılmadı: ${d.skipped_count} cari e-postasız${d.failed_count ? `, ${d.failed_count} başarısız` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e) || "Toplu gönderim başarısız"); }
    finally { setBulkBusy(false); }
  };
  const remindUnopened = async () => {
    setBulkBusy(true);
    try {
      const res = await api.post(`/accounting/cari/statement/remind-unopened`, { type });
      const d = res.data;
      if (d.reminded > 0) toast.success(`${d.reminded} açmayan cariye hatırlatma gönderildi`);
      else toast.message(d.candidates ? `${d.candidates} aday vardı ama e-posta gönderilemedi` : "Hatırlatılacak (gönderilip açılmamış) cari yok");
      load();
    } catch (e) { toast.error(apiError(e) || "Hatırlatma gönderilemedi"); }
    finally { setBulkBusy(false); }
  };
  const dlStatement = async (r, fmt) => {
    try {
      const res = await api.get(`/accounting/cari/${r.cari_type}/${r.cari_id}/export`, { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cari-ekstre-${(r.name || "cari").replace(/\s+/g, "_")}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Ekstre indirilemedi"); }
  };
  const waStatement = async (r) => {
    try {
      const res = await api.get(`/accounting/cari/${r.cari_type}/${r.cari_id}/statement/link`, { params: { channel: "whatsapp" } });
      const d = res.data;
      const pdf = d.short_pdf_url || d.pdf_url;
      const xlsx = d.short_xlsx_url || d.xlsx_url;
      let comp = "Groot ONE";
      try { comp = JSON.parse(localStorage.getItem("groot_company") || "{}").name || "Groot ONE"; } catch { /* default */ }
      let p = (d.phone || "").replace(/\D/g, "");
      if (p.startsWith("0")) p = "90" + p.slice(1);
      else if (p && !p.startsWith("90") && p.length === 10) p = "90" + p;
      const lines = [`Sayın ${d.name || ""},`, `${comp} hesap ekstreniz hazır:`];
      if (pdf) lines.push(`PDF: ${pdf}`);
      if (xlsx) lines.push(`Excel: ${xlsx}`);
      lines.push("(Bağlantılar 14 gün geçerlidir.)");
      const wa = `https://wa.me/${p}?text=${encodeURIComponent(lines.join("\n"))}`;
      window.open(wa, "_blank", "noopener");
      if (!p) toast.message("Carinin telefonu kayıtlı değil — WhatsApp'ta numarayı elle seçin.");
      load();
    } catch (e) { toast.error(apiError(e) || "WhatsApp bağlantısı üretilemedi"); }
  };
  const load = useCallback(() => {
    setLoading(true);
    api.get("/accounting/aging", { params: { type, q: q || undefined } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [type, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const items = data?.items || [];
  const t = data?.totals || { outstanding: 0, overdue: 0, count: 0, buckets: {} };
  const BUCKETS = [["current", "Güncel"], ["d30", "1-30 gün"], ["d60", "31-60 gün"], ["d90", "61-90 gün"], ["d90plus", "90+ gün"]];
  const TYPES = [["all", "Tümü"], ["customer", "Müşteri (Alacak)"], ["supplier", "Tedarikçi (Borç)"]];

  return (
    <div className="space-y-4" data-testid="aging-tab">
      <p className="text-sm text-slate-500">Alacak/Borç yaşlandırma — açık cari bakiyeleri vadeye göre kovalar. Vadesi geçmiş tutarlar <span className="font-semibold text-red-600">kırmızı</span> gösterilir. (Tedarikçi vadesi: ödeme vadesi günü; müşteri: peşin.)</p>

      <div className="flex flex-wrap items-center gap-2">
        {TYPES.map(([k, l]) => (
          <button key={k} data-testid={`aging-type-${k}`} onClick={() => setType(k)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${type === k ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>{l}</button>
        ))}
        <Input data-testid="aging-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari ara..." className="h-9 w-48" />
        {canExport && (
          <div className="ml-auto flex gap-2">
            <Button data-testid="aging-remind-config-btn" variant="outline" onClick={openRemConfig} title="Otomatik hatırlatma ayarları"
              className="h-9 px-2.5 text-slate-500 hover:text-slate-800">
              <Settings2 className="h-4 w-4" />
            </Button>
            <Button data-testid="aging-remind-btn" variant="outline" onClick={remindUnopened} disabled={bulkBusy || items.length === 0}
              className="h-9 border-amber-400 text-amber-600 hover:bg-amber-50">
              <Bell className="mr-1.5 h-4 w-4" /> Açmayanlara Hatırlat
            </Button>
            <Button data-testid="aging-bulk-email-btn" onClick={() => { setBulkResult(null); setBulkOpen(true); }} disabled={items.length === 0}
              className="h-9 bg-brand-green text-white hover:bg-brand-green/90">
              <Send className="mr-1.5 h-4 w-4" /> Tümüne Ekstre Gönder
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Kpi testid="aging-kpi-outstanding" label="Toplam Açık Bakiye" value={fmtTL(t.outstanding)} Icon={Wallet} accent="text-slate-900" />
        <Kpi testid="aging-kpi-overdue" label="Vadesi Geçmiş" value={fmtTL(t.overdue)} Icon={TrendingUp} accent={t.overdue > 0 ? "text-red-600" : "text-slate-900"} />
        <Kpi testid="aging-kpi-current" label="Güncel (vadesi gelmemiş)" value={fmtTL(t.buckets?.current || 0)} Icon={Coins} accent="text-emerald-600" />
        <Kpi testid="aging-kpi-count" label="Cari Sayısı" value={t.count} Icon={Users} accent="text-slate-900" />
      </div>

      {canExport && items.length > 0 && (
        <div data-testid="aging-tracking-strip" className="flex flex-wrap items-center gap-2 rounded-md bg-white px-3 py-2.5 ring-1 ring-inset ring-slate-200">
          <span className="mr-1 text-xs font-semibold text-slate-500">Ekstre Takip Panosu</span>
          {[
            ["track-sent", "Gönderildi", items.filter((i) => i.statement_sent_at).length, "bg-emerald-50 text-emerald-700 ring-emerald-200"],
            ["track-opened", "Görüldü", items.filter((i) => i.statement_opened_at).length, "bg-sky-50 text-sky-700 ring-sky-200"],
            ["track-unopened", "Açılmadı", items.filter((i) => i.statement_sent_at && !i.statement_opened_at).length, "bg-amber-50 text-amber-700 ring-amber-200"],
            ["track-notsent", "Hiç Gönderilmedi", items.filter((i) => !i.statement_sent_at).length, "bg-slate-50 text-slate-600 ring-slate-200"],
          ].map(([tid, label, val, cls]) => (
            <span key={tid} data-testid={tid} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${cls}`}>
              {label} <b className="tabular">{val}</b>
            </span>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-3 py-2.5">Cari</th>
              <th className="px-3 py-2.5">Tür</th>
              <th className="px-3 py-2.5 text-right">Açık Bakiye</th>
              {BUCKETS.map(([k, l]) => <th key={k} className="px-3 py-2.5 text-right">{l}</th>)}
              <th className="px-3 py-2.5 text-right">Gecikme</th>
              {canExport && <th className="px-3 py-2.5 text-right">Ekstre</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={canExport ? 10 : 9} className="px-3 py-10 text-center text-slate-400">Yükleniyor...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={canExport ? 10 : 9} className="px-3 py-10 text-center text-slate-400">Açık bakiyeli cari yok.</td></tr>
            ) : items.map((r) => (
              <tr key={`${r.cari_type}-${r.cari_id}`} data-testid={`aging-row-${r.cari_id}`}
                className={r.overdue ? "bg-red-50/60" : ""}>
                <td className="px-3 py-2.5 font-medium text-slate-800">{r.name}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.cari_type === "customer" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>
                    {r.cari_type === "customer" ? <User className="h-3 w-3" /> : <Building2 className="h-3 w-3" />}
                    {r.cari_type === "customer" ? "Müşteri" : "Tedarikçi"}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular font-semibold text-slate-800">{fmtTL(r.outstanding)}</td>
                {BUCKETS.map(([k]) => (
                  <td key={k} className={`px-3 py-2.5 text-right tabular ${k !== "current" && r.buckets[k] > 0 ? "font-semibold text-red-600" : "text-slate-500"}`}>
                    {r.buckets[k] > 0 ? fmtTL(r.buckets[k]) : "—"}
                  </td>
                ))}
                <td className="px-3 py-2.5 text-right">
                  {r.overdue
                    ? <span data-testid={`aging-overdue-${r.cari_id}`} className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">{r.max_days_overdue} gün</span>
                    : <span className="text-[11px] text-emerald-600">Güncel</span>}
                </td>
                {canExport && (
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`cari-ekstre-pdf-${r.cari_id}`} onClick={() => dlStatement(r, "pdf")} title="Ekstre PDF (indir)" className="mr-1 inline-flex items-center rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-red-600"><FileDown className="h-4 w-4" /></button>
                    <button data-testid={`cari-ekstre-xlsx-${r.cari_id}`} onClick={() => dlStatement(r, "xlsx")} title="Ekstre Excel (indir)" className="inline-flex items-center rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-emerald-600"><FileSpreadsheet className="h-4 w-4" /></button>
                    <button data-testid={`cari-ekstre-email-${r.cari_id}`} onClick={() => { setEmailRow(r); setEmailTo(""); }} title="Ekstreyi e-posta ile gönder" className="ml-1 inline-flex items-center rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-sky-600"><Mail className="h-4 w-4" /></button>
                    <button data-testid={`cari-ekstre-whatsapp-${r.cari_id}`} onClick={() => waStatement(r)} title="WhatsApp ile ekstre bağlantısı gönder" className="ml-1 inline-flex items-center rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-green-600"><MessageCircle className="h-4 w-4" /></button>
                    <button data-testid={`cari-ekstre-timeline-${r.cari_id}`} onClick={() => openTimeline(r)} title="Mutabakat zaman çizelgesi" className="ml-1 inline-flex items-center rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-indigo-600"><History className="h-4 w-4" /></button>
                    {r.statement_sent_at && !r.statement_opened_at && (
                      <span data-testid={`cari-ekstre-sent-${r.cari_id}`} title={`Gönderildi: ${new Date(r.statement_sent_at).toLocaleString("tr-TR")} · Henüz açılmadı`}
                        className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 align-middle"><Check className="h-3 w-3" /> Gönderildi</span>
                    )}
                    {r.statement_opened_at && (
                      <span data-testid={`cari-ekstre-opened-${r.cari_id}`} title={`Görüldü: ${new Date(r.statement_opened_at).toLocaleString("tr-TR")}${r.statement_opened_count ? ` · ${r.statement_opened_count} kez` : ""}`}
                        className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700 align-middle"><Eye className="h-3 w-3" /> Görüldü</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-800">
                <td className="px-3 py-2.5" colSpan={2}>Toplam</td>
                <td className="px-3 py-2.5 text-right tabular">{fmtTL(t.outstanding)}</td>
                {BUCKETS.map(([k]) => <td key={k} className={`px-3 py-2.5 text-right tabular ${k !== "current" && (t.buckets?.[k] || 0) > 0 ? "text-red-600" : ""}`}>{(t.buckets?.[k] || 0) > 0 ? fmtTL(t.buckets[k]) : "—"}</td>)}
                <td className="px-3 py-2.5 text-right tabular text-red-600">{t.overdue > 0 ? fmtTL(t.overdue) : "—"}</td>
                {canExport && <td className="px-3 py-2.5" />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <Dialog open={!!emailRow} onOpenChange={(o) => { if (!o) { setEmailRow(null); setEmailTo(""); } }}>
        <DialogContent data-testid="cari-ekstre-email-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cari Ekstresi Gönder</DialogTitle>
            <DialogDescription>
              {emailRow ? `${emailRow.name} carisinin hesap ekstresi e-posta ile gönderilecek.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-500">Alıcı e-posta (boş bırakırsanız carinin kayıtlı adresine gönderilir)</label>
            <Input data-testid="cari-ekstre-email-input" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="ornek@firma.com" />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button data-testid="cari-ekstre-email-cancel" variant="outline" onClick={() => { setEmailRow(null); setEmailTo(""); }}>Vazgeç</Button>
            <Button data-testid="cari-ekstre-email-send" onClick={sendStatement} disabled={emailBusy} className="bg-brand-green text-white hover:bg-brand-green/90">
              <Send className="mr-1.5 h-4 w-4" /> {emailBusy ? "Gönderiliyor..." : "Gönder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkResult(null); } }}>
        <DialogContent data-testid="aging-bulk-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Toplu Cari Ekstresi Gönder</DialogTitle>
            <DialogDescription>
              Seçili filtredeki ({TYPES.find(([k]) => k === type)?.[1] || "Tümü"}) tüm açık carilere hesap ekstresi e-posta ile gönderilir. E-posta adresi olmayan cariler atlanır (sahte adres üretilmez).
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input data-testid="aging-bulk-overdue" type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
            Yalnızca vadesi geçmiş carilere gönder
          </label>
          {bulkResult && (
            <div data-testid="aging-bulk-result" className="mt-2 space-y-1 rounded-md bg-slate-50 p-3 text-xs text-slate-600 ring-1 ring-inset ring-slate-200">
              <p><b className="text-emerald-700">{bulkResult.sent_count}</b> gönderildi · <b className="text-amber-600">{bulkResult.skipped_count}</b> e-postasız atlandı · <b className="text-red-600">{bulkResult.failed_count}</b> başarısız</p>
              {bulkResult.skipped_no_email?.length > 0 && (
                <p className="text-amber-600">E-postasız: {bulkResult.skipped_no_email.map((s) => s.name).join(", ")}</p>
              )}
            </div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button data-testid="aging-bulk-cancel" variant="outline" onClick={() => { setBulkOpen(false); setBulkResult(null); }}>Kapat</Button>
            <Button data-testid="aging-bulk-send" onClick={bulkSend} disabled={bulkBusy} className="bg-brand-green text-white hover:bg-brand-green/90">
              <Send className="mr-1.5 h-4 w-4" /> {bulkBusy ? "Gönderiliyor..." : "Gönder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={remOpen} onOpenChange={setRemOpen}>
        <DialogContent data-testid="aging-remind-config-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Otomatik Ekstre Hatırlatması</DialogTitle>
            <DialogDescription>
              Ay-sonu toplu gönderimden sonra ekstresini <b>açmayan</b> carilere otomatik hatırlatma e-postası yollanır. Dönem başına 1 kez çalışır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="aging-remind-enabled" type="checkbox" checked={remCfg.enabled} onChange={(e) => setRemCfg({ ...remCfg, enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
              Otomatik hatırlatmayı etkinleştir
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Gönderimden</span>
              <Input data-testid="aging-remind-days" type="number" min={1} max={30} value={remCfg.days} onChange={(e) => setRemCfg({ ...remCfg, days: e.target.value })} className="h-9 w-20" disabled={!remCfg.enabled} />
              <span className="text-sm text-slate-600">gün sonra açmayanlara</span>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button data-testid="aging-remind-config-cancel" variant="outline" onClick={() => setRemOpen(false)}>Vazgeç</Button>
            <Button data-testid="aging-remind-config-save" onClick={saveRemConfig} disabled={remBusy} className="bg-brand-green text-white hover:bg-brand-green/90">
              {remBusy ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tlRow} onOpenChange={(o) => { if (!o) { setTlRow(null); setTlEvents([]); } }}>
        <DialogContent data-testid="statement-timeline-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mutabakat Zaman Çizelgesi</DialogTitle>
            <DialogDescription>{tlRow ? `${tlRow.name} — ekstre gönderim / görülme geçmişi` : ""}</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto">
            {tlBusy ? (
              <p className="py-6 text-center text-sm text-slate-400">Yükleniyor...</p>
            ) : tlEvents.length === 0 ? (
              <p data-testid="timeline-empty" className="py-6 text-center text-sm text-slate-400">Bu cariye henüz ekstre gönderilmemiş.</p>
            ) : (
              <ol className="relative space-y-3 border-l border-slate-200 pl-4">
                {tlEvents.map((ev, i) => (
                  <li key={i} data-testid={`timeline-event-${i}`} className="relative">
                    <span className={`absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ${ev.event === "opened" ? "bg-sky-500" : "bg-emerald-500"}`} />
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${ev.event === "opened" ? "text-sky-700" : "text-emerald-700"}`}>{ev.event === "opened" ? "Görüldü / İndirildi" : "Gönderildi"}</span>
                      {ev.meta?.channel && <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{ev.meta.channel}</span>}
                    </div>
                    <p className="text-[11px] text-slate-400">{ev.at ? new Date(ev.at).toLocaleString("tr-TR") : ""}</p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChannelProfitTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); api.get("/accounting/channel-profitability", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [params.from_date, params.to_date]); // eslint-disable-line
  if (loading) return <div className="h-40 animate-pulse rounded-md bg-slate-200/60" />;
  if (!data) return null;
  const chans = data.channels || [];
  const t = data.totals || {};
  const chartData = chans.map((c) => ({ name: c.label, Ciro: c.revenue, "Kâr": c.profit }));
  const exportCP = async (fmt) => {
    try {
      const res = await api.get("/accounting/channel-profitability/export", { params: { ...params, format: fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `kanal-karlilik.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Kanal kârlılığı ${fmt === "pdf" ? "PDF" : "Excel"} olarak indirildi`);
    } catch { toast.error("Dışa aktarılamadı"); }
  };
  return (
    <div className="space-y-4" data-testid="acc-channel-profit">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="overline">Kanal Kârlılığı — dışa aktar</p>
        <div className="flex gap-2">
          <Button data-testid="cp-export-xlsx" onClick={() => exportCP("xlsx")} variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
          <Button data-testid="cp-export-pdf" onClick={() => exportCP("pdf")} variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-50"><FileDown className="mr-1.5 h-4 w-4" /> PDF</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi testid="cp-kpi-revenue" label="Toplam Ciro (net)" value={fmtTL(t.revenue)} Icon={TrendingUp} accent="text-brand-green" />
        <Kpi testid="cp-kpi-cost" label="Toplam Maliyet" value={fmtTL(t.cost)} Icon={Receipt} />
        <Kpi testid="cp-kpi-profit" label="Toplam Kâr" value={fmtTL(t.profit)} Icon={TrendingUp} accent="text-emerald-600" />
        <Kpi testid="cp-kpi-margin" label="Ortalama Marj" value={`%${t.margin_pct}`} Icon={Percent} accent="text-brand-orange" />
      </div>
      {chans.length === 0 ? (
        <div className="rounded-md border border-slate-200 p-8 text-center text-sm text-slate-400">Bu dönemde satış kaydı yok.</div>
      ) : (
        <>
          <div className="rounded-md border border-slate-200 p-4">
            <p className="overline mb-3">Kanal Bazlı Ciro &amp; Kâr</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Ciro" radius={[4, 4, 0, 0]} fill="#16a34a" />
                <Bar dataKey="Kâr" radius={[4, 4, 0, 0]} fill="#f97316" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500">
                <tr><th className="px-3 py-2">Kanal</th><th className="px-3 py-2 text-right">Satış</th><th className="px-3 py-2 text-right">Miktar</th><th className="px-3 py-2 text-right">Ciro (net)</th><th className="px-3 py-2 text-right">Maliyet</th><th className="px-3 py-2 text-right">Kâr</th><th className="px-3 py-2 text-right">Marj</th></tr>
              </thead>
              <tbody>
                {chans.map((c) => (
                  <tr key={c.channel} data-testid={`cp-row-${c.channel}`} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-800">{c.label}</td>
                    <td className="px-3 py-2 text-right tabular">{c.count}</td>
                    <td className="px-3 py-2 text-right tabular">{c.qty}</td>
                    <td className="px-3 py-2 text-right tabular font-semibold">{fmtTL(c.revenue)}</td>
                    <td className="px-3 py-2 text-right tabular text-slate-500">{fmtTL(c.cost)}</td>
                    <td className={`px-3 py-2 text-right tabular font-semibold ${c.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtTL(c.profit)}</td>
                    <td className="px-3 py-2 text-right tabular">%{c.margin_pct}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


function CountDiffTab({ params }) { // eslint-disable-line no-unused-vars
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); api.get("/accounting/count-diff-trends", { params: { months: 12 } }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []); // eslint-disable-line
  if (loading) return <div className="h-40 animate-pulse rounded-md bg-slate-200/60" />;
  if (!data) return null;
  const locs = data.by_location || [];
  const locChart = locs.slice(0, 10).map((l) => ({ name: l.name, Fire: l.shortage, Fazla: l.surplus }));
  const exportCD = async (fmt) => {
    try {
      const res = await api.get(`/accounting/count-diff-trends/export?format=${fmt}&months=12`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `sayim-farki.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };
  return (
    <div className="space-y-4" data-testid="acc-count-diff">
      <div className="flex items-center justify-end gap-2">
        <Button data-testid="cd-export-xlsx" onClick={() => exportCD("xlsx")} variant="outline" size="sm" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
        <Button data-testid="cd-export-pdf" onClick={() => exportCD("pdf")} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50"><FileDown className="mr-1.5 h-4 w-4" /> PDF</Button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Kpi testid="cd-kpi-shortage" label="Toplam Fire (Açık)" value={fmtTL(data.total_shortage)} Icon={Receipt} accent="text-red-600" />
        <Kpi testid="cd-kpi-surplus" label="Toplam Fazla" value={fmtTL(data.total_surplus)} Icon={TrendingUp} accent="text-emerald-600" />
        <Kpi testid="cd-kpi-net" label="Net Sayım Farkı" value={fmtTL(data.total_net)} Icon={Percent} accent={data.total_net < 0 ? "text-red-600" : "text-emerald-600"} />
      </div>
      <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="cd-monthly-chart">
        <p className="overline mb-3">Aylık Sayım Farkı — Fire / Fazla / Net (son 12 ay)</p>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data.series} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f5" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar name="Fazla" dataKey="surplus" fill="#04724d" radius={[4, 4, 0, 0]} maxBarSize={26} />
            <Bar name="Fire" dataKey="shortage" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={26} />
            <Line name="Net" type="monotone" dataKey="net" stroke="#f97316" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {locs.length > 0 ? (
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="cd-location-chart">
          <p className="overline mb-3">En Çok Kayıp Veren Depolar (Fire vs Fazla)</p>
          <ResponsiveContainer width="100%" height={Math.max(200, locChart.length * 44)}>
            <BarChart data={locChart} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f5" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#475569" }} />
              <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar name="Fire" dataKey="Fire" fill="#ef4444" radius={[0, 4, 4, 0]} maxBarSize={18} />
              <Bar name="Fazla" dataKey="Fazla" fill="#04724d" radius={[0, 4, 4, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                <th className="px-3 py-2 font-medium">Depo / Mağaza</th>
                <th className="px-3 py-2 text-right font-medium">Fire (Açık)</th>
                <th className="px-3 py-2 text-right font-medium">Fazla</th>
                <th className="px-3 py-2 text-right font-medium">Net</th>
              </tr></thead>
              <tbody>
                {locs.map((l) => (
                  <tr key={l.location_id} data-testid={`cd-loc-${l.location_id}`} className="border-b border-slate-50 last:border-0">
                    <td className="px-3 py-2 font-medium text-slate-700">{l.name}</td>
                    <td className="px-3 py-2 text-right tabular text-red-600">{fmtTL(l.shortage)}</td>
                    <td className="px-3 py-2 text-right tabular text-emerald-600">{fmtTL(l.surplus)}</td>
                    <td className={`px-3 py-2 text-right tabular font-semibold ${l.net < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtTL(l.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-slate-200 p-8 text-center text-sm text-slate-400" data-testid="cd-empty">Bu dönemde onaylanmış sayım farkı kaydı yok. Depo sayımı onaylandıkça fire/fazla maliyetleri burada grafikleşir.</div>
      )}
    </div>
  );
}

function OverviewTab({ params, onGoTab }) {  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [trend, setTrend] = useState(null);
  useEffect(() => { setLoading(true); api.get("/accounting/summary", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [params.from_date, params.to_date]); // eslint-disable-line
  useEffect(() => { api.get("/accounting/action-summary").then((r) => setAction(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/accounting/trends", { params: { months: 12 } }).then((r) => setTrend(r.data)).catch(() => {}); }, []);
  if (loading) return <div className="h-40 animate-pulse rounded-md bg-slate-200/60" />;
  if (!data) return null;
  const s = data.sales || {};
  const daily = (data.daily || []).map((x) => ({ ...x, gun: (x.date || "").slice(5) }));
  const vatRows = Object.entries(data.vat_by_rate || {}).sort((a, b) => a[0].localeCompare(b[0]));
  const exportAdvisor = async (fmt) => {
    try {
      const res = await api.get("/accounting/advisor-report/export", { params: { ...params, format: fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mali-musavir-rapor.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Mali Müşavir raporu indirildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Rapor oluşturulamadı"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-md bg-gradient-to-r from-brand-green/5 to-emerald-50 p-4 ring-1 ring-inset ring-emerald-200 sm:flex-row sm:items-center sm:justify-between" data-testid="acc-advisor-report">
        <div className="flex items-start gap-3">
          <FileText className="mt-0.5 h-5 w-5 shrink-0 text-brand-green" />
          <div>
            <p className="font-heading text-sm font-bold text-slate-800">Mali Müşavir Raporu</p>
            <p className="text-xs text-slate-500">Seçili dönem için genel paket: Gelir Tablosu · KDV · Gider kırılımı · Cari bakiyeler · e-Belge özeti. Excel çok sayfalıdır.</p>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button data-testid="advisor-export-xlsx" onClick={() => exportAdvisor("xlsx")} variant="outline" size="sm" className="border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel Paketi</Button>
          <Button data-testid="advisor-export-pdf" onClick={() => exportAdvisor("pdf")} variant="outline" size="sm" className="border-red-300 bg-white text-red-700 hover:bg-red-50"><FileDown className="mr-1.5 h-4 w-4" /> PDF Paketi</Button>
        </div>
      </div>
      {action && (action.incoming_pending > 0 || action.payroll?.pending_count > 0 || ((action.due?.overdue || 0) + (action.due?.upcoming || 0)) > 0) ? (
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="acc-action-summary">
          <p className="overline mb-3">Bekleyen Mali Aksiyonlar</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <button data-testid="action-incoming" onClick={() => onGoTab?.("incoming")} disabled={!action.incoming_pending}
              className={`flex items-center justify-between rounded-md border p-3 text-left transition-colors ${action.incoming_pending ? "border-amber-300 bg-amber-50 hover:bg-amber-100" : "border-slate-200 bg-slate-50 opacity-60"}`}>
              <div><p className="text-xs text-slate-500">Onay bekleyen gelen fatura</p><p className="font-heading text-2xl font-bold text-slate-800">{action.incoming_pending}</p></div>
              <FileText className="h-6 w-6 text-amber-500" />
            </button>
            <button data-testid="action-payroll" onClick={() => navigate("/personel")} disabled={!action.payroll?.pending_count}
              className={`flex items-center justify-between rounded-md border p-3 text-left transition-colors ${action.payroll?.pending_count ? "border-sky-300 bg-sky-50 hover:bg-sky-100" : "border-slate-200 bg-slate-50 opacity-60"}`}>
              <div><p className="text-xs text-slate-500">İşlenmemiş bordro (bu ay)</p><p className="font-heading text-2xl font-bold text-slate-800">{action.payroll?.pending_count || 0}<span className="ml-1 text-sm font-medium text-slate-400">personel</span></p>{action.payroll?.pending_total > 0 && <p className="text-xs text-slate-400">{fmtTL(action.payroll.pending_total)}</p>}</div>
              <Coins className="h-6 w-6 text-sky-500" />
            </button>
            <button data-testid="action-due" onClick={() => onGoTab?.("due")} disabled={!((action.due?.overdue || 0) + (action.due?.upcoming || 0))}
              className={`flex items-center justify-between rounded-md border p-3 text-left transition-colors ${((action.due?.overdue || 0) + (action.due?.upcoming || 0)) ? "border-red-300 bg-red-50 hover:bg-red-100" : "border-slate-200 bg-slate-50 opacity-60"}`}>
              <div><p className="text-xs text-slate-500">Çek/Senet vadesi ({action.due?.window_days}g)</p><p className="font-heading text-2xl font-bold text-slate-800">{(action.due?.overdue || 0) + (action.due?.upcoming || 0)}</p><p className="text-xs text-red-500">{action.due?.overdue || 0} gecikmiş · {action.due?.upcoming || 0} yaklaşan</p></div>
              <Wallet className="h-6 w-6 text-red-500" />
            </button>
          </div>
        </div>
      ) : action ? (
        <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-700 ring-1 ring-inset ring-emerald-200" data-testid="acc-action-clear">✓ Bekleyen mali aksiyon yok — gelen fatura, bordro ve çek/senet vadeleri güncel.</div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testid="acc-kpi-gross" label="Brüt Ciro" value={fmtTL(s.gross)} sub={`${s.count || 0} satış`} Icon={TrendingUp} accent="text-brand-green" />
        <Kpi testid="acc-kpi-net" label="Net (KDV hariç)" value={fmtTL(s.net)} Icon={Receipt} />
        <Kpi testid="acc-kpi-vat" label="Toplam KDV" value={fmtTL(s.vat)} Icon={Percent} accent="text-brand-orange" />
        <Kpi testid="acc-kpi-profit" label="Brüt Kâr" value={fmtTL(data.gross_profit)} sub={`Marj %${data.gross_margin_pct}`} Icon={TrendingUp} accent="text-emerald-600" />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
          <p className="overline mb-3">Günlük Ciro</p>
          {daily.length === 0 ? <p className="py-10 text-center text-sm text-slate-400">Satış yok</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={daily} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f5" />
                <XAxis dataKey="gun" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="total" fill="#04724d" radius={[4, 4, 0, 0]} maxBarSize={42} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
          <p className="overline mb-3">KDV Kırılımı</p>
          {vatRows.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">Veri yok</p> : (
            <div className="divide-y divide-slate-100">
              {vatRows.map(([rate, amt]) => (<div key={rate} className="flex items-center justify-between py-2 text-sm"><span className="text-slate-600">KDV {rate}</span><span className="tabular font-semibold">{fmtTL(amt)}</span></div>))}
            </div>
          )}
        </div>
      </div>
      {trend && (trend.series || []).length > 0 && (
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="acc-monthly-trend">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="overline">Aylık Gelir / Gider / Net (son 12 ay)</p>
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="font-semibold text-emerald-600">Gelir {fmtTL(trend.total_revenue)}</span>
              <span className="font-semibold text-red-600">Gider {fmtTL(trend.total_expense)}</span>
              <span className={`font-semibold ${trend.total_net < 0 ? "text-red-600" : "text-slate-800"}`}>Net {fmtTL(trend.total_net)}</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={trend.series} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f5" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={44} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
              <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar name="Gelir" dataKey="revenue" fill="#04724d" radius={[4, 4, 0, 0]} maxBarSize={26} />
              <Bar name="Gider" dataKey="expense" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={26} />
              <Line name="Net" type="monotone" dataKey="net" stroke="#f97316" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
          {(trend.expense_by_category || []).length > 0 && (
            <div className="mt-3 border-t border-slate-100 pt-3" data-testid="acc-expense-by-category">
              <p className="overline mb-2">Gider Dağılımı (Kategori)</p>
              <div className="flex flex-wrap gap-2">
                {trend.expense_by_category.map((c) => (
                  <span key={c.category} data-testid={`acc-expcat-${c.category}`} className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-100">
                    {c.category} <span className="tabular font-bold">{fmtTL(c.amount)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {(data.expense_by_location || []).length > 0 && (
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="acc-expense-by-location">
          <div className="mb-3 flex items-center justify-between">
            <p className="overline">Depo / Mağaza Bazında Gider</p>
            <span className="text-sm font-semibold text-red-600">Toplam Gider: {fmtTL(data.expense_total)}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
                <th className="px-3 py-2 font-medium">Lokasyon</th>
                <th className="px-3 py-2 text-right font-medium">Gider</th>
                <th className="px-3 py-2 text-right font-medium">Gelir</th>
                <th className="px-3 py-2 text-right font-medium">Net</th>
                <th className="px-3 py-2 text-right font-medium">Pay</th>
              </tr></thead>
              <tbody>
                {data.expense_by_location.map((l) => (
                  <tr key={l.location_id} data-testid={`acc-exploc-${l.location_id}`} className="border-b border-slate-50 last:border-0">
                    <td className="px-3 py-2 font-medium text-slate-700">{l.name}</td>
                    <td className="px-3 py-2 text-right tabular text-red-600">{fmtTL(l.expense)}</td>
                    <td className="px-3 py-2 text-right tabular text-emerald-600">{fmtTL(l.income)}</td>
                    <td className={`px-3 py-2 text-right tabular font-semibold ${(l.income - l.expense) < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtTL(l.income - l.expense)}</td>
                    <td className="px-3 py-2 text-right tabular text-slate-400">%{data.expense_total ? Math.round(l.expense / data.expense_total * 100) : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function LedgerTab({ range, canWrite, openEntry, setOpenEntry }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [filter, setFilter] = useState("all"); // all | income | expense
  const [loading, setLoading] = useState(true);
  const [printDoc, setPrintDoc] = useState(null);
  useEffect(() => {
    if (!printDoc) return;
    const t = setTimeout(() => { printFinanceNode(); setPrintDoc(null); }, 60);
    return () => clearTimeout(t);
  }, [printDoc]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { from_date: range[0], to_date: range[1], page_size: 500 };
    if (filter === "income" || filter === "expense") params.kind = filter;
    api.get("/accounting/entries", { params }).then((r) => { setRows(r.data.items || []); setTotals(r.data.totals || {}); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [range, filter]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { try { const { data } = await api.delete(`/accounting/entries/${id}`); toast.success(data?.pending_approval ? "Silme onaya gönderildi." : "Kayıt silindi"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const visible = sortRows(rows.filter((r) => r.kind === "income" || r.kind === "expense"), {
    date: (e) => e.date || "",
    kind: (e) => e.kind || "",
    category: (e) => e.category || "",
    description: (e) => e.description || "",
    amount: (e) => Number(e.amount) || 0,
    vat: (e) => Number(e.vat_amount) || 0,
    gross: (e) => Number(e.gross) || 0,
    account: (e) => e.account || "",
  });
  const paged = usePaged(visible);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          {[["all", "Tümü"], ["income", "Gelir"], ["expense", "Gider"]].map(([k, l]) => (
            <button key={k} data-testid={`ledger-filter-${k}`} onClick={() => setFilter(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${filter === k ? "bg-slate-800 text-white" : "bg-white text-slate-500 ring-1 ring-inset ring-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Kpi testid="ledger-total-net" label="Net Toplam" value={fmtTL(totals.net)} Icon={Receipt} />
        <Kpi testid="ledger-total-vat" label="KDV Toplam" value={fmtTL(totals.vat)} Icon={Percent} accent="text-brand-orange" />
        <Kpi testid="ledger-total-gross" label="Brüt Toplam" value={fmtTL(totals.gross)} Icon={Wallet} accent="text-brand-green" />
      </div>

      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr><SortTh label="Tarih" k="date" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="ledger-sort-date" /><SortTh label="Tür" k="kind" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="ledger-sort-kind" /><SortTh label="Kategori" k="category" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="ledger-sort-category" /><SortTh label="Açıklama" k="description" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="ledger-sort-desc" /><SortTh label="Net" k="amount" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="ledger-sort-amount" /><SortTh label="KDV" k="vat" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="ledger-sort-vat" /><SortTh label="Brüt" k="gross" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="ledger-sort-gross" /><SortTh label="Hesap" k="account" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="ledger-sort-account" /><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Yükleniyor...</td></tr>}
            {!loading && visible.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Bu dönemde kayıt yok. "Yeni Kayıt" ile gelir/gider ekleyin.</td></tr>}
            {paged.slice.map((e) => (
              <tr key={e.id} data-testid={`ledger-row-${e.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-500">{e.date}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${e.kind === "income" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>{e.kind === "income" ? "Gelir" : "Gider"}</span></td>
                <td className="px-4 py-3 text-slate-600">{e.category || "—"}</td>
                <td className="px-4 py-3 text-slate-600">
                  {e.description || "—"}
                  {e.location_name && <span data-testid={`ledger-loc-${e.id}`} className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{e.location_name}</span>}
                </td>
                <td className="px-4 py-3 text-right tabular">{fmtCur(e.amount, e.currency)}</td>
                <td className="px-4 py-3 text-right tabular text-slate-500">{fmtCur(e.vat_amount, e.currency)} <span className="text-[10px]">%{Math.round(e.vat_rate)}</span></td>
                <td className={`px-4 py-3 text-right tabular font-bold ${e.kind === "income" ? "text-emerald-700" : "text-red-600"}`}>{fmtCur(e.gross, e.currency)}</td>
                <td className="px-4 py-3 text-slate-500">{ACC_LABEL[e.account] || e.account}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <button data-testid={`ledger-makbuz-${e.id}`} onClick={() => setPrintDoc({ entry: e, type: "makbuz" })} title="Makbuz Yazdır" className="rounded-md p-1.5 text-slate-400 hover:bg-brand-green/10 hover:text-brand-green"><Receipt className="h-4 w-4" /></button>
                    <button data-testid={`ledger-dekont-${e.id}`} onClick={() => setPrintDoc({ entry: e, type: "dekont" })} title="Dekont Yazdır" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Printer className="h-4 w-4" /></button>
                    {canWrite && <button data-testid={`ledger-del-${e.id}`} onClick={() => del(e.id)} className="rounded-md p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={paged.page} pageSize={paged.pageSize} total={paged.total}
        onPage={paged.setPage} onPageSize={paged.setPageSize} testid="ledger-pagination" />
      {printDoc && <div style={{ position: "fixed", left: "-10000px", top: 0 }} aria-hidden><FinanceDocA5 entry={printDoc.entry} type={printDoc.type} /></div>}
      {openEntry && <EntryDialog onClose={() => setOpenEntry(false)} onSaved={() => { setOpenEntry(false); load(); }} />}
    </div>
  );
}

function EntryDialog({ onClose, onSaved }) {
  const today = isoDate(new Date());
  const { locations: accLocs } = useActiveLocation();
  const [form, setForm] = useState({ kind: "expense", date: today, category: "", description: "", amount: "", vat_rate: "20", account: "cash", location_id: "", due_date: "", cari_type: "", cari_id: "", document_url: "", doc_no: "", is_advance: false, advance_period: today.slice(0, 7), currency: activeCurrency(), fx_rate: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cariList, setCariList] = useState([]);
  const [cariQ, setCariQ] = useState("");
  const [cariBal, setCariBal] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isCari = form.kind === "debit" || form.kind === "credit";

  useEffect(() => {
    if (isCari && form.cari_type && form.cari_id) {
      api.get("/accounting/cari-balance", { params: { type: form.cari_type, cari_id: form.cari_id } })
        .then((r) => setCariBal(r.data.balance)).catch(() => setCariBal(null));
    } else { setCariBal(null); }
  }, [isCari, form.cari_type, form.cari_id]);

  useEffect(() => {
    if (!form.cari_type) { setCariList([]); return; }
    const t = setTimeout(() => {
      api.get("/accounting/cari-options", { params: { type: form.cari_type, q: cariQ || undefined } })
        .then((r) => setCariList(r.data || [])).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [form.cari_type, cariQ]);

  const KLBL = { income: "Gelir", expense: "Gider", debit: "Cari Borç", credit: "Cari Alacak" };
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("document_url", data.url);
      // QR şablon eşleme: yüklenen görsel bir boş şablonun QR'ını taşıyorsa belge no'yu oku ve bağla
      let matched = null;
      try { const p = parseDocNoQr(await scanQrFromFile(file)); if (p?.doc_no) matched = p.doc_no; } catch { /* yok say */ }
      if (matched) { set("doc_no", matched); toast.success(`Belge yüklendi · QR okundu · Belge No: ${matched}`); }
      else toast.success(form.doc_no ? "Belge yüklendi" : "Belge yüklendi — Belge No gönderirken otomatik atanır");
    } catch (er) { toast.error(apiError(er.response?.data?.detail) || "Belge yüklenemedi"); } finally { setUploading(false); }
  };
  const save = async () => {
    const amt = parseFloat(String(form.amount).replace(",", "."));
    if (!amt || amt <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    if (!form.description.trim()) { toast.error("Açıklama zorunlu"); return; }
    if (isCari && (!form.cari_type || !form.cari_id)) { toast.error("Cari (müşteri / tedarikçi / personel) seçin"); return; }
    if (!form.document_url) { toast.error("Kayıt için belge (fiş / fatura / dekont) yükleyin — zorunlu"); return; }
    setBusy(true);
    try {
      const payload = { ...form, amount: amt, vat_rate: parseFloat(form.vat_rate) || 0 };
      if (!payload.cari_type) { payload.cari_type = null; payload.cari_id = null; }
      if (payload.cari_type !== "personnel") { payload.is_advance = false; }
      const { data } = await api.post("/accounting/entries", payload);
      if (data?.pending_approval) toast.success("Kayıt onaya gönderildi (onaylanınca işlenir).");
      else toast.success(`${KLBL[form.kind]} kaydedildi${data?.doc_no ? ` · Belge No: ${data.doc_no}` : ""}`);
      if (data?.advance_warning?.message) toast.warning(data.advance_warning.message, { duration: 8000 });
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const cats = form.kind === "income" ? INC_CATS : EXP_CATS;
  const KINDS = [["expense", "Gider", "bg-red-600"], ["income", "Gelir", "bg-emerald-600"], ["debit", "Cari Borç (+)", "bg-amber-600"], ["credit", "Cari Alacak (−)", "bg-sky-600"]];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="entry-dialog" className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Yeni Muhasebe Kaydı</DialogTitle>
          <DialogDescription>{isCari ? "Cari borç/tahsilat için müşteri, tedarikçi veya personel seçin. Personel için 'maaş avansı' işaretlenirse ay sonu maaştan otomatik düşülür." : "Gelir/gider girin (tutar KDV hariç). İstersen müşteri/tedarikçi ilişkilendir (personel işlemleri Cari'de yapılır)."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {KINDS.map(([k, l, c]) => (
              <button key={k} data-testid={`entry-kind-${k}`} onClick={() => { set("kind", k); if ((k === "income" || k === "expense") && form.cari_type === "personnel") { set("cari_type", ""); set("cari_id", ""); set("is_advance", false); } }}
                className={`rounded-md py-2 text-sm font-semibold ${form.kind === k ? `${c} text-white` : "bg-slate-100 text-slate-600"}`}>{l}</button>
            ))}
          </div>

          <div className="rounded-md bg-slate-50 p-2.5 ring-1 ring-inset ring-slate-200">
            <label className="mb-1 block text-xs font-medium text-slate-500">Müşteri / Tedarikçi{isCari ? " / Personel (zorunlu)" : " (opsiyonel)"}</label>
            <div className="flex flex-wrap gap-1">
              {[["", "Yok"], ["customer", "Müşteri"], ["supplier", "Tedarikçi"], ["personnel", "Personel"]].filter(([k]) => !(isCari && k === "") && !(!isCari && k === "personnel")).map(([k, l]) => (
                <button key={k || "none"} data-testid={`entry-cari-type-${k || "none"}`} onClick={() => { set("cari_type", k); set("cari_id", ""); setCariQ(""); if (k !== "personnel") set("is_advance", false); }}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${form.cari_type === k ? "bg-slate-800 text-white" : "bg-white text-slate-500 ring-1 ring-inset ring-slate-200"}`}>{l}</button>
              ))}
            </div>
            {form.cari_type && (() => {
              const selected = cariList.find((c) => c.id === form.cari_id);
              const shown = cariQ.trim() ? cariList : cariList.slice(0, 5);
              return (
                <div className="mt-2 space-y-1.5">
                  {form.cari_id && selected ? (
                    <div data-testid="entry-cari-selected" className="flex items-center justify-between rounded-md border border-brand-green/40 bg-brand-green/5 px-2.5 py-2 text-sm">
                      <span className="font-medium text-slate-700">{selected.name}</span>
                      <button type="button" data-testid="entry-cari-change" onClick={() => { set("cari_id", ""); setCariQ(""); }} className="text-xs font-semibold text-brand-green hover:underline">değiştir</button>
                    </div>
                  ) : (
                    <>
                      <Input data-testid="entry-cari-search" value={cariQ} onChange={(e) => setCariQ(e.target.value)}
                        placeholder={`${form.cari_type === "customer" ? "Müşteri" : form.cari_type === "personnel" ? "Personel" : "Tedarikçi"} ara (isim/kod)…`} className="h-9" autoComplete="off" />
                      <div data-testid="entry-cari-results" className="max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-white">
                        {shown.length === 0 ? (
                          <p className="px-2.5 py-2 text-xs text-slate-400">Sonuç yok</p>
                        ) : shown.map((c) => (
                          <button key={c.id} type="button" data-testid={`entry-cari-opt-${c.id}`} onClick={() => set("cari_id", c.id)}
                            className="flex w-full items-center justify-between px-2.5 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50">
                            <span className="truncate">{c.name}</span>
                            {c.code && <span className="ml-2 shrink-0 font-mono text-[10px] text-slate-400">{c.code}</span>}
                          </button>
                        ))}
                        {!cariQ.trim() && cariList.length > 5 && (
                          <p className="border-t border-slate-100 px-2.5 py-1.5 text-[11px] text-slate-400">İlk 5 gösteriliyor — aramak için yazın ({cariList.length} kayıt)</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            {form.cari_type === "personnel" && form.kind === "debit" && (
              <div className="mt-2 rounded-md bg-amber-50 p-2 ring-1 ring-inset ring-amber-200">
                <label className="flex items-center gap-2 text-xs font-medium text-amber-800">
                  <input type="checkbox" data-testid="entry-advance-toggle" checked={!!form.is_advance} onChange={(e) => set("is_advance", e.target.checked)} className="h-3.5 w-3.5 accent-amber-600" />
                  Bu bir maaş avansı — ay sonu maaştan kesilsin
                </label>
                {form.is_advance && (
                  <div className="mt-2">
                    <label className="mb-1 block text-[11px] text-amber-700">Kesileceği maaş dönemi</label>
                    <input type="month" data-testid="entry-advance-period" value={form.advance_period} onChange={(e) => set("advance_period", e.target.value)} className="h-8 rounded-md border border-amber-300 bg-white px-2 text-sm" />
                    <p className="mt-1 text-[10px] text-amber-700/80">Avans, {form.advance_period || "seçili"} dönemi maaşından otomatik düşülür ve mahsup edilince kapanır.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div><label className="mb-1 block text-xs text-slate-500">Tarih</label><DatePickerTR testid="entry-date" value={form.date} onChange={(v) => set("date", v)} /></div>
            <div><label className="mb-1 block text-xs text-slate-500">Ödeme Hesabı</label>
              <select data-testid="entry-account" value={form.account} onChange={(e) => set("account", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                <option value="cash">Nakit</option><option value="bank">Banka</option><option value="credit">Açık/Kredi</option><option value="cek">Çek</option><option value="senet">Senet</option>
              </select>
            </div>
          </div>

          {!isCari && (
            <div><label className="mb-1 block text-xs text-slate-500">Kategori</label>
              <input data-testid="entry-category" list="acc-cats" value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Kategori" className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm" />
              <datalist id="acc-cats">{cats.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
          )}
          {!isCari && accLocs.length > 0 && (
            <div><label className="mb-1 block text-xs text-slate-500">Depo / Mağaza <span className="text-slate-400">(opsiyonel — gider kırılımı)</span></label>
              <select data-testid="entry-location" value={form.location_id} onChange={(e) => set("location_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                <option value="">Genel / Atanmamış</option>
                {accLocs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}
          <div><label className="mb-1 block text-xs text-slate-500">Açıklama <span className="font-semibold text-amber-600">*zorunlu</span></label><Input data-testid="entry-desc" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="İşlem açıklaması (zorunlu)" /></div>
          {(form.account === "cek" || form.account === "senet") && (
            <div data-testid="entry-due-wrap"><label className="mb-1 block text-xs text-slate-500">Vade Tarihi ({form.account === "cek" ? "Çek" : "Senet"})</label>
              <DatePickerTR testid="entry-due" value={form.due_date} onChange={(v) => set("due_date", v)} />
              <p className="mt-1 text-[11px] text-slate-400">Vade tarihi girilirse "Vadeler" sekmesinde takip edilir.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div><label className="mb-1 block text-xs text-slate-500">{isCari ? `Tutar ${symbolForCurrency(form.currency)}` : `Tutar (KDV hariç ${curSym()})`}</label><Input data-testid="entry-amount" inputMode="decimal" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0,00" className="tabular" /></div>
            {!isCari && (
              <div><label className="mb-1 block text-xs text-slate-500">KDV Oranı</label>
                <select data-testid="entry-vat" value={form.vat_rate} onChange={(e) => set("vat_rate", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                  <option value="0">%0</option><option value="1">%1</option><option value="10">%10</option><option value="20">%20</option>
                </select>
              </div>
            )}
            {isCari && (
              <div><label className="mb-1 block text-xs text-slate-500">Para Birimi</label>
                <select data-testid="entry-currency" value={form.currency} onChange={(e) => set("currency", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                  {Object.keys(CURRENCIES).map((c) => <option key={c} value={c}>{symbolForCurrency(c)} {c}</option>)}
                </select>
                {form.currency !== "TRY" && (
                  <div className="mt-1 flex items-center gap-1">
                    <Input data-testid="entry-fx" inputMode="decimal" value={form.fx_rate} onChange={(e) => set("fx_rate", e.target.value)} placeholder="Kur (₺)" title="1 birim = ? TL karşılığı" className="h-8 tabular" />
                    <LiveFxButton currency={form.currency} onRate={(r) => set("fx_rate", String(r))} testid="entry-fx-live" />
                  </div>
                )}
              </div>
            )}
          </div>
          {isCari && form.currency === "TRY" && cariBal !== null && (
            <div data-testid="entry-cari-balance" className="rounded-md bg-slate-50 p-2.5 text-xs ring-1 ring-inset ring-slate-200">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Mevcut bakiye</span>
                <span className={`tabular font-semibold ${cariBal > 0 ? "text-emerald-700" : cariBal < 0 ? "text-red-600" : "text-slate-500"}`}>{fmtTL(Math.abs(cariBal))} {cariBal > 0 ? "(cari bize borçlu)" : cariBal < 0 ? "(biz cari'ye borçluyuz)" : "(kapalı)"}</span>
              </div>
              {(parseFloat(String(form.amount).replace(",", ".")) || 0) > 0 && (() => {
                const amt = parseFloat(String(form.amount).replace(",", ".")) || 0;
                const proj = cariBal + (form.kind === "debit" ? amt : -amt);
                return <div className="mt-1 flex items-center justify-between border-t border-slate-200 pt-1"><span className="text-slate-500">İşlem sonrası</span><span className={`tabular font-bold ${proj > 0 ? "text-emerald-700" : proj < 0 ? "text-red-600" : "text-slate-500"}`}>{fmtTL(Math.abs(proj))} {proj > 0 ? "(cari bize borçlu)" : proj < 0 ? "(biz cari'ye borçluyuz)" : "(kapalı)"}</span></div>;
              })()}
            </div>
          )}
          <div className={`space-y-2 rounded-md px-3 py-2.5 ring-1 ring-inset ${!form.document_url ? "bg-amber-50 ring-amber-300" : "bg-emerald-50 ring-emerald-200"}`}>
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs text-slate-600">Belge (fiş / fatura / dekont) <span className="font-semibold text-amber-600">*zorunlu</span></label>
              <button type="button" data-testid="entry-template-print"
                onClick={async () => { const no = await printBlankTemplate("finance"); if (no) { set("doc_no", no); toast.success(`Boş şablon · Belge No: ${no}`); } }}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-green hover:underline"><Printer className="h-3.5 w-3.5" /> Boş Şablon Yazdır</button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <label className="cursor-pointer rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">
                {uploading ? "Yükleniyor..." : (form.document_url ? "Belgeyi Değiştir" : "Fiş / Belge Yükle")}
                <input data-testid="entry-doc-upload" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="hidden" onChange={uploadDoc} />
              </label>
              {form.document_url && <span data-testid="entry-doc-ok" className="text-[11px] font-semibold text-emerald-600">✓ yüklendi</span>}
            </div>
            {form.doc_no && <div data-testid="entry-doc-no" className="rounded bg-white px-2 py-1 text-[11px] text-slate-600 ring-1 ring-inset ring-slate-200">Belge No: <b className="text-brand-green">{form.doc_no}</b> <span className="text-slate-400">(otomatik)</span></div>}
            <p className="text-[10px] text-slate-500">Boş şablonu yazdırıp doldurup imzalayın ve geri yükleyin — <b>veya</b> hazır fiş/fatura görselini doğrudan yükleyin; sistem belge no'yu otomatik atar.</p>
          </div>
          <Button data-testid="entry-save" onClick={save} disabled={busy} className="w-full bg-brand-green text-white hover:bg-brand-green-600">{busy ? "Kaydediliyor..." : "Kaydet"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PLTab({ params }) {
  const [d, setD] = useState(null); const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); api.get("/accounting/pl", { params }).then((r) => setD(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [params.from_date, params.to_date]); // eslint-disable-line
  if (loading) return <div className="h-40 animate-pulse rounded-md bg-slate-200/60" />;
  if (!d) return null;
  const profit = d.net_profit >= 0;
  const exportPL = async (fmt) => {
    try {
      const res = await api.get("/accounting/pl/export", { params: { ...params, format: fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `gelir-tablosu.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Gelir tablosu ${fmt === "pdf" ? "PDF" : "Excel"} olarak indirildi`);
    } catch { toast.error("Dışa aktarılamadı"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="overline">Gelir Tablosu — dışa aktar</p>
        <div className="flex gap-2">
          <Button data-testid="pl-export-xlsx" onClick={() => exportPL("xlsx")} variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
          <Button data-testid="pl-export-pdf" onClick={() => exportPL("pdf")} variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-50"><FileDown className="mr-1.5 h-4 w-4" /> PDF</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2 rounded-md bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="pl-statement">
        <p className="overline mb-2">Kâr / Zarar Tablosu</p>
        <StmtRow label="Satış Geliri (KDV hariç)" value={fmtTL(d.sales_revenue_net)} />
        <StmtRow label="Diğer Gelirler" value={fmtTL(d.other_income)} />
        <StmtRow label="Toplam Gelir" value={fmtTL(d.total_income)} strong accent="text-slate-900" />
        <StmtRow label="(−) Satılan Malın Maliyeti (SMM)" value={fmtTL(d.cogs)} accent="text-red-600" />
        <StmtRow label="Brüt Kâr" value={fmtTL(d.gross_profit)} strong accent="text-emerald-700" />
        <StmtRow label="(−) Giderler" value={fmtTL(d.expenses)} accent="text-red-600" />
        <StmtRow label={profit ? "NET KÂR" : "NET ZARAR"} value={fmtTL(d.net_profit)} strong accent={profit ? "text-emerald-700" : "text-red-600"} />
      </div>
      <div className="space-y-3">
        <Kpi testid="pl-net-profit" label={profit ? "Net Kâr" : "Net Zarar"} value={fmtTL(d.net_profit)} sub={`Net marj %${d.net_margin_pct}`} Icon={TrendingUp} accent={profit ? "text-emerald-600" : "text-red-600"} />
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
          <p className="overline mb-2">Giderler (kategori)</p>
          {Object.keys(d.expenses_by_category || {}).length === 0 ? <p className="py-4 text-center text-sm text-slate-400">Gider kaydı yok</p> : (
            <div className="divide-y divide-slate-100">
              {Object.entries(d.expenses_by_category).sort((a, b) => b[1] - a[1]).map(([c, v]) => (
                <div key={c} className="flex items-center justify-between py-2 text-sm"><span className="text-slate-600">{c}</span><span className="tabular font-semibold text-red-600">{fmtTL(v)}</span></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
}

function VatTab({ params }) {
  const [d, setD] = useState(null); const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); api.get("/accounting/vat", { params }).then((r) => setD(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [params.from_date, params.to_date]); // eslint-disable-line
  if (loading) return <div className="h-40 animate-pulse rounded-md bg-slate-200/60" />;
  if (!d) return null;
  const payable = d.net >= 0;
  const rows = Object.entries(d.by_rate || {}).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2 rounded-md bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="vat-statement">
        <p className="overline mb-2">KDV Beyanı (özet)</p>
        <StmtRow label="Hesaplanan KDV (satış + gelir)" value={fmtTL(d.collected)} accent="text-brand-orange" />
        <StmtRow label="— Satışlardan" value={fmtTL(d.collected_sales)} indent />
        <StmtRow label="— Diğer gelirlerden" value={fmtTL(d.collected_income)} indent />
        <StmtRow label="(−) İndirilecek KDV (giderler)" value={fmtTL(d.deductible)} accent="text-emerald-700" />
        <StmtRow label={d.status} value={fmtTL(Math.abs(d.net))} strong accent={payable ? "text-red-600" : "text-emerald-700"} />
      </div>
      <div className="space-y-3">
        <Kpi testid="vat-net" label={d.status} value={fmtTL(Math.abs(d.net))} Icon={Percent} accent={payable ? "text-red-600" : "text-emerald-700"} />
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
          <p className="overline mb-2">Hesaplanan KDV — oran</p>
          {rows.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">Veri yok</p> : (
            <div className="divide-y divide-slate-100">{rows.map(([r, v]) => (<div key={r} className="flex items-center justify-between py-2 text-sm"><span className="text-slate-600">KDV {r}</span><span className="tabular font-semibold">{fmtTL(v)}</span></div>))}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function CariTab({ range, canWrite }) {
  const [list, setList] = useState([]);
  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/accounting/cari", { params: { type, q: q || undefined } }).then((r) => setList(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [type, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {[["all", "Tümü"], ["customer", "Müşteri"], ["supplier", "Tedarikçi"]].map(([k, l]) => (
            <button key={k} data-testid={`cari-type-${k}`} onClick={() => setType(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${type === k ? "bg-slate-800 text-white" : "bg-white text-slate-500 ring-1 ring-inset ring-slate-200"}`}>{l}</button>
          ))}
        </div>
        <Input data-testid="cari-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari ara..." className="h-9 max-w-xs" />
        <div data-testid="cari-legend" className="ml-auto flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Borç <span className="text-slate-400">(cari bize borçlu)</span></span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Alacak <span className="text-slate-400">(biz borçluyuz)</span></span>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr><SortTh label="Cari" k="name" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="cari-sort-name" /><SortTh label="Tür" k="cari_type" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="cari-sort-type" /><SortTh label="Borç" k="debit" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="cari-sort-debit" /><SortTh label="Alacak" k="credit" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="cari-sort-credit" /><SortTh label="Bakiye" k="balance" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="cari-sort-balance" /><SortTh label="Satış Toplamı" k="sales_total" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="cari-sort-sales" /><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Yükleniyor...</td></tr>}
            {!loading && list.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Hareketli cari yok. "Yeni Kayıt" ile cari borç/tahsilat ekleyin.</td></tr>}
            {sortRows(list, {
              name: (c) => c.name || "",
              cari_type: (c) => c.cari_type || "",
              debit: (c) => Number(c.debit) || 0,
              credit: (c) => Number(c.credit) || 0,
              balance: (c) => Number(c.balance) || 0,
              sales_total: (c) => Number(c.sales_total) || 0,
            }).map((c) => (
              <tr key={`${c.cari_type}-${c.cari_id}`} data-testid={`cari-row-${c.cari_id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(c)}>
                <td className="px-4 py-3 font-medium text-slate-700">{c.name}</td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs text-slate-500">{c.cari_type === "customer" ? <User className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}{c.cari_type === "customer" ? "Müşteri" : "Tedarikçi"}</span></td>
                <td className="px-4 py-3 text-right tabular text-emerald-700">{fmtTL(c.debit)}</td>
                <td className="px-4 py-3 text-right tabular text-red-600">{fmtTL(c.credit)}</td>
                <td className="px-4 py-3 text-right tabular font-bold text-slate-800">{fmtTL(Math.abs(c.balance))} <span className="text-[10px] font-medium text-slate-500">{c.balance > 0 ? "borçlu" : c.balance < 0 ? "alacaklı" : ""}</span>
                  {c.multi_currency && (c.currencies || []).filter((x) => x.currency !== "TRY" && Math.abs(x.balance) > 0.005).map((x) => (
                    <div key={x.currency} data-testid={`cari-cur-${c.cari_id}-${x.currency}`} className="text-[10px] font-semibold text-slate-600">{fmtCur(Math.abs(x.balance), x.currency)} {x.balance > 0 ? "borçlu" : "alacaklı"}</div>
                  ))}
                </td>
                <td className="px-4 py-3 text-right tabular text-slate-500">{c.cari_type === "customer" ? fmtTL(c.sales_total) : "—"}</td>
                <td className="px-4 py-3 text-right"><Eye className="ml-auto h-4 w-4 text-slate-300" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {detail && <CariDetailDialog cari={detail} range={range} canWrite={canWrite} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}

function CariDetailDialog({ cari, range, canWrite, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get(`/accounting/cari/${cari.cari_type}/${cari.cari_id}`).then((r) => setData(r.data)).catch(() => {}); }, [cari]);
  useEffect(() => { load(); }, [load]);

  const [from, setFrom] = useState(range?.[0] || "");
  const [to, setTo] = useState(range?.[1] || "");
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/accounting/cari/${cari.cari_type}/${cari.cari_id}/export`,
        { params: { format: "pdf", from_date: from || undefined, to_date: to || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Ekstre oluşturulamadı"); } finally { setPdfBusy(false); }
  };

  const bal = data?.balance ?? 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cari-detail-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">{cari.name}</DialogTitle>
          <DialogDescription>
            {cari.cari_type === "customer" ? "Müşteri" : "Tedarikçi"} cari hesabı ·
            Bakiye: <b className={bal > 0 ? "text-emerald-700" : bal < 0 ? "text-red-600" : ""}> {fmtTL(Math.abs(bal))} {bal > 0 ? "(borçlu)" : bal < 0 ? "(alacaklı)" : ""}</b>
            {data?.multi_currency && (data.by_currency || []).filter((x) => x.currency !== "TRY").map((x) => (
              <span key={x.currency} data-testid={`cari-detail-cur-${x.currency}`} className={`ml-2 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${x.balance > 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{fmtCur(Math.abs(x.balance), x.currency)} {x.balance > 0 ? "borçlu" : "alacaklı"}</span>
            ))}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md bg-slate-50 p-2.5 text-xs text-slate-500 ring-1 ring-inset ring-slate-200">
          Cari hareketleri (borç / tahsilat / ödeme) yalnızca <b>Yeni Kayıt</b> ekranından, onay akışıyla eklenir. Bu ekran salt görüntülemedir.
        </div>
        <div className="max-h-72 overflow-y-auto rounded-md ring-1 ring-inset ring-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500"><tr><th className="px-3 py-2 font-medium">Tarih</th><th className="px-3 py-2 font-medium">Açıklama</th><th className="px-3 py-2 text-right font-medium">Borç</th><th className="px-3 py-2 text-right font-medium">Alacak</th><th className="px-3 py-2 text-right font-medium">Bakiye</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {!data && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Yükleniyor...</td></tr>}
              {data && data.ledger.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Cari hareketi yok.</td></tr>}
              {data && data.ledger.map((l) => (
                <tr key={l.id} data-testid={`cari-ledger-${l.id}`}>
                  <td className="px-3 py-2 text-slate-500">{l.date}</td>
                  <td className="px-3 py-2 text-slate-600">{l.description || l.category || "—"}{l.currency && l.currency !== "TRY" ? <span className="ml-1 rounded bg-slate-100 px-1 text-[9px] font-bold text-slate-500">{l.currency}</span> : null}</td>
                  <td className="px-3 py-2 text-right tabular text-emerald-700">{l.debit ? fmtCur(l.debit, l.currency) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular text-red-600">{l.credit ? fmtCur(l.credit, l.currency) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular font-semibold">{fmtCur(l.balance, l.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && cari.cari_type === "customer" && data.sales.length > 0 && (
          <div>
            <p className="overline mb-1">İlişkili Satışlar (bilgi)</p>
            <div className="max-h-32 overflow-y-auto rounded-md ring-1 ring-inset ring-slate-200">
              <table className="w-full text-sm"><tbody className="divide-y divide-slate-100">
                {data.sales.map((s) => (<tr key={s.id}><td className="px-3 py-1.5 font-mono text-xs text-slate-500">{s.sale_no}</td><td className="px-3 py-1.5 text-slate-400 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString("tr-TR") : ""}</td><td className="px-3 py-1.5 text-right tabular font-semibold text-brand-green">{fmtTL(s.total)}</td></tr>))}
              </tbody></table>
            </div>
          </div>
        )}
        <div className="mt-1 flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-3">
          <span className="mr-auto text-xs font-medium text-slate-500">Ekstre dönemi:</span>
          <DatePickerTR testid="cari-pdf-from" value={from} onChange={(v) => setFrom(v)} className="mt-0 h-9 w-40" />
          <span className="text-xs text-slate-400">–</span>
          <DatePickerTR testid="cari-pdf-to" value={to} onChange={(v) => setTo(v)} className="mt-0 h-9 w-40" />
          <Button data-testid="cari-pdf-btn" onClick={downloadPdf} disabled={pdfBusy} className="bg-brand-green text-white hover:bg-brand-green-600">
            <FileDown className="mr-1.5 h-4 w-4" /> {pdfBusy ? "Hazırlanıyor..." : "PDF Ekstre / Yazdır"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CalendarTab() {
  const [month, setMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`; });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { setLoading(true); api.get("/accounting/calendar", { params: { month } }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false)); }, [month]);
  const shift = (delta) => { const [y, m] = month.split("-").map(Number); const d = new Date(y, m - 1 + delta, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); };
  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const daysInMonth = new Date(y, m, 0).getDate();
  const startDow = (first.getDay() + 6) % 7;
  const byDay = {};
  (data?.events || []).forEach((e) => { const day = Number(String(e.date).slice(8, 10)); (byDay[day] = byDay[day] || []).push(e); });
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let dd = 1; dd <= daysInMonth; dd++) cells.push(dd);
  const WD = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
  const today = data?.today;
  const s = data?.summary || {};
  const monthLabel = first.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  return (
    <div className="space-y-4" data-testid="acc-calendar">
      <p className="text-sm text-slate-500">Çek/senet vadeleri ve tüm varlık evrak bitiş tarihleri (sözleşme, sigorta, ruhsat vb.) tek aylık takvimde. Ay değiştirmek için okları kullanın.</p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => shift(-1)} className="rounded-md p-1.5 text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
          <span data-testid="cal-month" className="min-w-[150px] text-center font-heading text-lg font-bold capitalize text-slate-800">{monthLabel}</span>
          <button data-testid="cal-next" onClick={() => shift(1)} className="rounded-md p-1.5 text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Tahsilat</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Ödeme</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Evrak Vadesi</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Kpi testid="cal-kpi-due" label="Çek/Senet Vadesi" value={s.due ?? 0} Icon={CalendarDays} accent="text-slate-900" />
        <Kpi testid="cal-kpi-doc" label="Evrak Vadesi" value={s.doc ?? 0} Icon={FileText} accent="text-amber-600" />
        <Kpi testid="cal-kpi-in" label="Tahsil (açık)" value={fmtTL(s.due_in)} Icon={TrendingUp} accent="text-emerald-700" />
        <Kpi testid="cal-kpi-out" label="Ödenecek (açık)" value={fmtTL(s.due_out)} Icon={Receipt} accent="text-red-600" />
      </div>
      {loading ? <div className="h-80 animate-pulse rounded-md bg-slate-200/60" /> : (
        <div className="overflow-hidden rounded-md bg-white ring-1 ring-inset ring-slate-200">
          <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50 text-center text-[11px] font-semibold text-slate-500">
            {WD.map((w) => <div key={w} className="py-2">{w}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((day, i) => {
              if (day === null) return <div key={`e${i}`} className="min-h-[92px] border-b border-r border-slate-50 bg-slate-50/40" />;
              const ds = `${month}-${String(day).padStart(2, "0")}`;
              const evs = byDay[day] || [];
              const isToday = ds === today;
              return (
                <div key={day} data-testid={`cal-day-${day}`} className={`min-h-[92px] border-b border-r border-slate-50 p-1 ${isToday ? "bg-brand-green/5 ring-1 ring-inset ring-brand-green/30" : ""}`}>
                  <div className={`mb-1 text-right text-[11px] font-semibold ${isToday ? "text-brand-green" : "text-slate-400"}`}>{day}</div>
                  <div className="space-y-0.5">
                    {evs.slice(0, 3).map((e, j) => {
                      const cls = e.category === "doc" ? "bg-amber-100 text-amber-800"
                        : e.direction === "in" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700";
                      const label = e.category === "doc" ? e.title : `${e.type === "cek" ? "Çek" : "Senet"} · ${fmtTL(e.amount)}${e.settled ? " ✓" : ""}`;
                      return <div key={j} title={e.title} className={`truncate rounded px-1 py-0.5 text-[10px] font-medium ${cls} ${e.settled ? "line-through opacity-60" : ""}`}>{label}</div>;
                    })}
                    {evs.length > 3 && <div className="text-[10px] text-slate-400">+{evs.length - 3} daha</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FxReportTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => { setLoading(true); api.get("/accounting/fx-report").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false)); }, []);
  useEffect(() => { reload(); }, [reload]);
  if (loading) return <div className="h-40 animate-pulse rounded-md bg-slate-200/60" />;
  const items = data?.items || [];
  const plfx = data?.pl_fx || [];
  const t = data?.totals || {};
  return (
    <div className="space-y-4" data-testid="acc-fx-report">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-3xl text-sm text-slate-500">Dövizli açık cari bakiyeleri <b>kayıt kuru</b> (deftere işlenmiş TL) ile <b>bugünkü canlı kur</b> karşılaştırılır; fark <b>gerçekleşmemiş kur farkı</b>dır (FX gain/loss). {data?.rates_source?.length ? `Kaynak: ${data.rates_source.join(", ")}` : ""}</p>
        <Button data-testid="fx-reload" variant="outline" size="sm" onClick={reload} className="border-slate-300"><RefreshCw className="mr-1.5 h-4 w-4" /> Kurları Yenile</Button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi testid="fx-kpi-booked" label="Kayıt Kuru (TL)" value={fmtTL(t.booked_tl)} Icon={Coins} accent="text-slate-900" />
        <Kpi testid="fx-kpi-current" label="Güncel Değer (TL)" value={fmtTL(t.current_tl)} Icon={Globe} accent="text-sky-700" />
        <Kpi testid="fx-kpi-gl" label="Kur Farkı (FX)" value={`${(t.fx_gain_loss || 0) >= 0 ? "+" : ""}${fmtTL(t.fx_gain_loss)}`} Icon={TrendingUp} accent={(t.fx_gain_loss || 0) >= 0 ? "text-emerald-600" : "text-red-600"} />
      </div>
      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr><th className="px-3 py-2.5">Döviz</th><th className="px-3 py-2.5 text-right">Açık Bakiye (orijinal)</th><th className="px-3 py-2.5 text-right">Kayıt Kuru TL</th><th className="px-3 py-2.5 text-right">Güncel Kur</th><th className="px-3 py-2.5 text-right">Güncel TL</th><th className="px-3 py-2.5 text-right">Kur Farkı</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 ? <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400">Açık dövizli cari bakiyesi yok.</td></tr> : items.map((r) => (
              <tr key={r.currency} data-testid={`fx-row-${r.currency}`}>
                <td className="px-3 py-2.5 font-semibold text-slate-800">{r.symbol} {r.currency}</td>
                <td className="px-3 py-2.5 text-right tabular">{fmtCur(r.orig_balance, r.currency)}</td>
                <td className="px-3 py-2.5 text-right tabular text-slate-600">{fmtTL(r.booked_tl)}</td>
                <td className="px-3 py-2.5 text-right tabular text-slate-500">{r.current_rate}</td>
                <td className="px-3 py-2.5 text-right tabular font-semibold">{fmtTL(r.current_tl)}</td>
                <td className={`px-3 py-2.5 text-right tabular font-bold ${r.fx_gain_loss >= 0 ? "text-emerald-600" : "text-red-600"}`}>{r.fx_gain_loss >= 0 ? "+" : ""}{fmtTL(r.fx_gain_loss)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {plfx.length > 0 && (
        <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200" data-testid="fx-plfx">
          <p className="overline px-3 pt-3">Dövizli Gelir/Gider (orijinal vs TL — kâr/zarara işlenen)</p>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr><th className="px-3 py-2.5">Döviz</th><th className="px-3 py-2.5 text-right">Gelir (orijinal)</th><th className="px-3 py-2.5 text-right">Gelir (TL)</th><th className="px-3 py-2.5 text-right">Gider (orijinal)</th><th className="px-3 py-2.5 text-right">Gider (TL)</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {plfx.map((r) => (
                <tr key={r.currency} data-testid={`fx-pl-${r.currency}`}>
                  <td className="px-3 py-2.5 font-semibold text-slate-800">{r.symbol} {r.currency}</td>
                  <td className="px-3 py-2.5 text-right tabular">{fmtCur(r.income_orig, r.currency)}</td>
                  <td className="px-3 py-2.5 text-right tabular text-emerald-700">{fmtTL(r.income_tl)}</td>
                  <td className="px-3 py-2.5 text-right tabular">{fmtCur(r.expense_orig, r.currency)}</td>
                  <td className="px-3 py-2.5 text-right tabular text-red-600">{fmtTL(r.expense_tl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DueTab({ canWrite }) {
  const [data, setData] = useState({ items: [], summary: {} });
  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(true);
  const [win, setWin] = useState(3);
  const [winEdit, setWinEdit] = useState("");
  const load = useCallback(() => {
    setLoading(true);
    api.get("/accounting/due", { params: { status } }).then((r) => setData(r.data || { items: [], summary: {} })).catch(() => {}).finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/settings/due-reminder-days").then((r) => { setWin(r.data.days); setWinEdit(String(r.data.days)); }).catch(() => {}); }, []);
  const saveWin = async () => {
    const v = Number(winEdit); if (!v || v < 1) return;
    try { const r = await api.put("/settings/due-reminder-days", { days: v }); setWin(r.data.days); setWinEdit(String(r.data.days)); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Yetkiniz yok"); }
  };
  const settle = async (id) => {
    try { await api.post(`/accounting/entries/${id}/settle`); toast.success("Durum güncellendi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const s = data.summary || {};
  const STCLS = { overdue: "bg-red-100 text-red-700", upcoming: "bg-amber-100 text-amber-700", scheduled: "bg-slate-100 text-slate-600", settled: "bg-emerald-100 text-emerald-700" };
  const STLBL = { overdue: "Gecikmiş", upcoming: "Yaklaşan", scheduled: "İleri tarihli", settled: "Kapandı" };
  const FILTERS = [["open", "Açık"], ["overdue", "Gecikmiş"], ["upcoming", "Yaklaşan"], ["scheduled", "İleri tarihli"], ["settled", "Kapanmış"], ["all", "Tümü"]];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi testid="due-overdue" label="Gecikmiş Adet" value={s.overdue ?? 0} Icon={Coins} accent="text-red-600" />
        <Kpi testid="due-upcoming" label={`Yaklaşan (${win} gün)`} value={s.upcoming ?? 0} Icon={Coins} accent="text-brand-orange" />
        <Kpi testid="due-in" label="Tahsil Edilecek (açık)" value={fmtTL(s.in_total)} Icon={TrendingUp} accent="text-emerald-700" />
        <Kpi testid="due-out" label="Ödenecek (açık)" value={fmtTL(s.out_total)} Icon={Receipt} accent="text-red-600" />
      </div>
      {canWrite && (
        <div data-testid="due-window-box" className="flex flex-wrap items-center gap-2 rounded-md bg-white px-3 py-2 text-xs text-slate-500 ring-1 ring-inset ring-slate-200">
          <span>Vade hatırlatması kaç gün kala başlasın:</span>
          <input data-testid="due-window-input" type="number" min="1" value={winEdit} onChange={(e) => setWinEdit(e.target.value)}
            className="h-7 w-16 rounded border border-slate-200 px-2 text-center tabular focus:outline-none focus:ring-1 focus:ring-brand-green" />
          <span className="text-slate-400">gün</span>
          {String(win) !== winEdit && <button data-testid="due-window-save" onClick={saveWin} className="rounded bg-brand-green px-2 py-0.5 text-[11px] font-bold text-white hover:bg-emerald-700">Kaydet</button>}
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`due-filter-${k}`} onClick={() => setStatus(k)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${status === k ? "bg-slate-800 text-white" : "bg-white text-slate-500 ring-1 ring-inset ring-slate-200"}`}>{l}</button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr><SortTh label="Vade" k="due_date" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="due-sort-date" /><SortTh label="Tür" k="account" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="due-sort-account" /><SortTh label="Yön" k="direction" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="due-sort-direction" /><SortTh label="Cari / Kategori" k="cari" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="due-sort-cari" /><SortTh label="Açıklama" k="description" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="due-sort-desc" /><SortTh label="Tutar" k="amount" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="due-sort-amount" /><SortTh label="Durum" k="status" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="due-sort-status" /><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Yükleniyor...</td></tr>}
            {!loading && (data.items || []).length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Vadeli çek/senet kaydı yok. Gelir/Gider veya Cari eklerken ödeme türünü Çek/Senet seçip vade tarihi girin.</td></tr>}
            {sortRows(data.items || [], {
              due_date: (e) => e.due_date || "",
              account: (e) => e.account || "",
              direction: (e) => e.direction || "",
              cari: (e) => e.cari_name || e.category || "",
              description: (e) => e.description || "",
              amount: (e) => Number(e.gross || e.amount) || 0,
              status: (e) => e.due_status || "",
            }).map((e) => (
              <tr key={e.id} data-testid={`due-row-${e.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-700">{e.due_date}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{e.account === "cek" ? "Çek" : "Senet"}</span></td>
                <td className="px-4 py-3">{e.direction === "in" ? <span className="text-xs font-semibold text-emerald-700">Tahsilat</span> : <span className="text-xs font-semibold text-red-600">Ödeme</span>}</td>
                <td className="px-4 py-3 text-slate-600">{e.cari_name || e.category || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{e.description || "—"}</td>
                <td className={`px-4 py-3 text-right tabular font-bold ${e.direction === "in" ? "text-emerald-700" : "text-red-600"}`}>{fmtTL(e.gross || e.amount)}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STCLS[e.due_status]}`}>{STLBL[e.due_status]}</span></td>
                <td className="px-4 py-3 text-right">
                  {canWrite && (e.due_status === "settled" ? (
                    <button data-testid={`due-unsettle-${e.id}`} onClick={() => settle(e.id)} className="text-[11px] font-semibold text-slate-400 hover:text-slate-600">Geri al</button>
                  ) : (
                    <button data-testid={`due-settle-${e.id}`} onClick={() => settle(e.id)} className="inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50">{e.direction === "in" ? "Tahsil edildi" : "Ödendi"}</button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
